/**
 * Centralized Token Manager
 * - Handles token refresh with singleton pattern
 * - Prevents race conditions with promise caching
 * - Manages request queue during token refresh
 * - Proactive token refresh before expiry
 * - Automatic request retry after successful refresh
 */

import { useAuthStore } from '@/stores/auth'
import { authLogger } from '@/utils/logger'

interface QueuedRequest {
  resolve: (token: string | null) => void
  reject: (error: any) => void
}

class TokenManager {
  private refreshPromise: Promise<boolean> | null = null
  private requestQueue: QueuedRequest[] = []
  private isRefreshing = false
  private refreshTimer: ReturnType<typeof setTimeout> | null = null
  private isProactiveRefreshEnabled = true

  /**
   * Get access token and check if it's expired
   * @returns {string | null} - Access token or null if expired/invalid
   */
  getAccessToken(): string | null {
    if (typeof window === 'undefined')
      return null

    const token = (useLocalStorage('accessToken', null) as any).value
    if (!token)
      return null

    try {
      // Decode JWT to check expiry
      const base64Url = token.split('.')[1]
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
      const payload = JSON.parse(atob(base64))
      const expiryTime = payload.exp * 1000 // Convert to milliseconds
      const currentTime = Date.now()

      // Schedule proactive refresh if enabled
      if (this.isProactiveRefreshEnabled)
        this.scheduleProactiveRefresh(expiryTime)

      // Check if token will expire in next 30 seconds
      const bufferTime = 30 * 1000 // 30 seconds buffer
      if (expiryTime - currentTime < bufferTime) {
        authLogger.debug('Token will expire soon, need refresh')

        return null
      }

      return token
    }
    catch (error) {
      authLogger.error('Failed to decode token:', error)

      return null
    }
  }

  /**
   * Schedule proactive token refresh before expiry
   * - Refreshes 5 minutes before token expires
   * - Prevents unnecessary 401 errors
   */
  private scheduleProactiveRefresh(expiryTime: number) {
    // Clear existing timer
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer)
      this.refreshTimer = null
    }

    const currentTime = Date.now()
    const timeUntilExpiry = expiryTime - currentTime

    // Refresh 5 minutes before expiry (or halfway if token lifetime is less than 10 minutes)
    const refreshBeforeExpiry = Math.min(5 * 60 * 1000, timeUntilExpiry / 2)
    const refreshTime = timeUntilExpiry - refreshBeforeExpiry

    // Only schedule if we have enough time
    if (refreshTime > 0 && refreshTime < 24 * 60 * 60 * 1000) { // Max 24 hours
      authLogger.debug(`Proactive refresh scheduled in ${Math.round(refreshTime / 1000 / 60)} minutes`)

      this.refreshTimer = setTimeout(async () => {
        authLogger.debug('Proactive token refresh triggered')

        const success = await this.refreshToken()

        if (success)
          authLogger.info('Proactive token refresh successful')
        else
          authLogger.warn('Proactive token refresh failed')
      }, refreshTime)
    }
  }

  /**
   * Enable or disable proactive token refresh
   */
  setProactiveRefresh(enabled: boolean) {
    this.isProactiveRefreshEnabled = enabled

    if (!enabled && this.refreshTimer) {
      clearTimeout(this.refreshTimer)
      this.refreshTimer = null
    }
  }

  /**
   * Refresh access token with singleton pattern
   * - Only one refresh request at a time
   * - Queue other requests until refresh completes
   * @returns {Promise<boolean>} - True if refresh successful
   */
  async refreshToken(): Promise<boolean> {
    // If already refreshing, return the existing promise
    if (this.refreshPromise)
      return this.refreshPromise

    // Mark as refreshing
    this.isRefreshing = true

    // Create new refresh promise
    this.refreshPromise = (async () => {
      try {
        const authStore = useAuthStore()

        const success = await authStore.refreshAccessToken()

        if (success) {
          // Resolve all queued requests with new token
          const newToken = this.getAccessToken()

          this.requestQueue.forEach(({ resolve }) => resolve(newToken))
        }
        else {
          // Reject all queued requests
          this.requestQueue.forEach(({ reject }) =>
            reject(new Error('Token refresh failed')),
          )

          // ✅ Clear tokens BEFORE redirect to prevent loop
          await this.clearTokens(true) // skipRedirect=true to prevent double redirect

          if (typeof window !== 'undefined')
            window.location.replace('/login')
        }

        return success
      }
      catch (error) {
        console.error('❌ Token refresh error (exception thrown):', error)

        // Reject all queued requests
        this.requestQueue.forEach(({ reject }) => reject(error))

        // ✅ Clear tokens BEFORE redirect to prevent loop
        await this.clearTokens(true) // skipRedirect=true to prevent double redirect

        if (typeof window !== 'undefined')
          window.location.replace('/login')

        return false
      }
      finally {
        // Clear state
        this.isRefreshing = false
        this.refreshPromise = null
        this.requestQueue = []
      }
    })()

    return this.refreshPromise
  }

  /**
   * Get valid access token (refresh if needed)
   * @returns {Promise<string | null>} - Valid access token or null
   */
  async getValidAccessToken(): Promise<string | null> {
    // Try to get current token
    const currentToken = this.getAccessToken()
    if (currentToken)
      return currentToken

    // Token expired or invalid, need refresh

    // If refresh is in progress, queue this request
    if (this.isRefreshing) {
      return new Promise((resolve, reject) => {
        this.requestQueue.push({ resolve, reject })
      })
    }

    // Start refresh
    const success = await this.refreshToken()

    if (success)
      return this.getAccessToken()

    return null
  }

  /**
   * Clear all tokens and logout
   * @param skipRedirect - If true, skip redirect to login (let caller handle it)
   */
  async clearTokens(skipRedirect = false) {
    // Clear proactive refresh timer
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer)
      this.refreshTimer = null
    }

    const authStore = useAuthStore()

    await authStore.logout(skipRedirect)
  }

  /**
   * Initialize token manager
   * - Should be called after login
   * - Starts proactive refresh scheduling
   */
  initialize() {
    const token = this.getAccessToken()
  }
}

// Export singleton instance
export const tokenManager = new TokenManager()
